// M7 — Documents + Review (plan §4.5.4 + §4.6.3 + §11.2).

import { z } from "zod";

import { type QcMeta, QC_STATUSES } from "./qc.js";
import { KpiTextWarningSchema } from "./kpi.js";

export const DocumentTypeSchema = z.enum(["full", "partial", "draft"]);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

export const DocumentInfoSchema = z.object({
  id: z.string().uuid(),
  type: DocumentTypeSchema,
  version: z.number().int().nonnegative(),
  fileSizeBytes: z.number().int().nonnegative(),
  sectionCodes: z.array(z.string()).nullable(),
  generatedAt: z.string(),
  generatedBy: z.string().uuid().nullable(),
  supersededAt: z.string().nullable(),
  // TODO-KPI-13: belgede donmuş KPI manifesti var mı (`kpi_snapshot IS NOT NULL`). FE
  // `DocumentKpiLink`'i yalnız true iken çizer — manifest payload'ı listeye taşınmaz.
  hasKpiSnapshot: z.boolean(),
  // K6 — "Taslak Word (ön izleme)": tip `partial` + taslak işareti (migration yok; storage_key
  // `partial-taslak-` öneki). Listelerde "Taslak" rozeti; nihai rapordan ayrı tutulur.
  isDraft: z.boolean().optional(),
});
export type DocumentInfo = z.infer<typeof DocumentInfoSchema>;

export const DocumentExportResponseSchema = z.object({
  document: DocumentInfoSchema,
  downloadUrl: z.string().url(),
});
export type DocumentExportResponse = z.infer<typeof DocumentExportResponseSchema>;

export const PartialExportRequestSchema = z.object({
  sectionCodes: z.array(z.string().min(1)).min(1),
});
export type PartialExportRequest = z.infer<typeof PartialExportRequestSchema>;

export const DocumentListResponseSchema = z.object({
  documents: z.array(DocumentInfoSchema),
});
export type DocumentListResponse = z.infer<typeof DocumentListResponseSchema>;

// Review state machine
export const ReviewActionSchema = z.enum(["approve", "reject"]);
export type ReviewAction = z.infer<typeof ReviewActionSchema>;

export const ReviewRequestSchema = z
  .object({
    action: ReviewActionSchema,
    note: z.string().max(2000).optional(),
  })
  .refine(
    (d) => d.action !== "reject" || (typeof d.note === "string" && d.note.trim().length > 0),
    {
      message: "Reject aksiyonu için note (sebep) zorunludur",
      path: ["note"],
    },
  );
export type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

export const BulkApproveResponseSchema = z.object({
  approvedCount: z.number().int().nonnegative(),
  // Final review I1 — yeniden üretimi süren (queued/running) bekleyen bölümler atlanır ve sayılır.
  skippedBusyCount: z.number().int().nonnegative(),
});
export type BulkApproveResponse = z.infer<typeof BulkApproveResponseSchema>;

// Reopen (admin-only + reason zorunlu — karar #3)
export const ReopenRequestSchema = z.object({
  reason: z.string().min(5).max(1000),
});
export type ReopenRequest = z.infer<typeof ReopenRequestSchema>;

export const ReopenResponseSchema = z.object({
  reopenedSectionCount: z.number().int().nonnegative(),
  supersededDocumentCount: z.number().int().nonnegative(),
});
export type ReopenResponse = z.infer<typeof ReopenResponseSchema>;

// Content update (TipTap editor → PATCH /sections/:code/content)
export const SectionContentUpdateRequestSchema = z.object({
  content: z.string().max(100_000),
});
export type SectionContentUpdateRequest = z.infer<typeof SectionContentUpdateRequestSchema>;

// Review ekranı için section row — M6 generation satırlarının review view'ı
// M11 — attachmentsUsed: bu section'da LLM'e gönderilen kanıt belgeler (review UI)
/** M17 T3 — bayat bölümde değişen KPI girdisi: kaydedilen sürüm (from) → güncel değer (to). */
export const KpiStaleInputSchema = z.object({
  label: z.string(),
  year: z.number().int(),
  from: z.number().nullable(),
  to: z.number().nullable(),
  unit: z.string().nullable(),
});
export type KpiStaleInput = z.infer<typeof KpiStaleInputSchema>;

export const ReviewSectionSchema = z.object({
  id: z.string().uuid(),
  sectionCode: z.string(),
  version: z.number().int().positive(),
  content: z.string().nullable(),
  contentOriginal: z.string().nullable(),
  assembledPrompt: z.string().nullable(),
  reviewStatus: z.enum(["pending_review", "approved", "rejected"]),
  reviewNote: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewedBy: z.string().uuid().nullable(),
  generationStatus: z.enum(["queued", "running", "success", "failed"]),
  attachmentsUsed: z
    .array(
      z.object({
        attachmentId: z.string().uuid(),
        filename: z.string(),
        mimeType: z.string(),
        sizeBytes: z.number().int().nonnegative(),
        questionCode: z.string(),
      }),
    )
    .default([]),
  // Coverage guard — yanıt anchor'larının (sayı/özel ad/kısaltma) çıktıda bulunma
  // oranı; düşükse review UI "eksik yansıma" uyarısı gösterir.
  coverageRatio: z.number().min(0).max(1).nullable().default(null),
  coverageMissing: z.array(z.string()).default([]),
  // T13 — metin ↔ KPI tablosu sayı tutarlılığı (projede KPI seti varsa canlı hesaplanır; bloklamaz)
  kpiWarnings: z.array(KpiTextWarningSchema).default([]),
  // M17 T3 — üretimden sonra kullandığı KPI değerlerinden biri değişti (trigger, migration 0057).
  // Yalnız iç aktöre; danışman "Rakamları doğruladım" ile temizler (kpiStaleAck izi kalır).
  kpiStale: z.boolean().default(false),
  kpiStaleInputs: z.array(KpiStaleInputSchema).default([]),
  kpiStaleAck: z.object({ byName: z.string(), at: z.string() }).nullable().default(null),
  // Onarım turu (repair pass) — coverage eşik altındaysa worker'ın otomatik
  // eksik-veri yerleştirme denemesi; FE küçük bilgi rozeti gösterir.
  repairMeta: z
    .object({
      attempted: z.boolean(),
      accepted: z.boolean(),
      ratioBefore: z.number().nullable(),
      ratioAfter: z.number().nullable().optional(),
      reason: z.string().optional(),
    })
    .nullable()
    .default(null),
  // KK (Kalite Kontrol) Agent — spec §3/§5. qcMeta interface (Zod şeması değil,
  // sadece worker/route içinde üretilip taşınır) → z.custom ile tip korunur.
  qcStatus: z.enum(QC_STATUSES).nullable(),
  qcCandidate: z.string().nullable(),
  qcMeta: z.custom<QcMeta>().nullable(),
  qcFailureMessage: z.string().nullable(),
});
export type ReviewSection = z.infer<typeof ReviewSectionSchema>;

export const ReviewListResponseSchema = z.object({
  sections: z.array(ReviewSectionSchema),
});
export type ReviewListResponse = z.infer<typeof ReviewListResponseSchema>;
